/* global $ */
import { Controller } from '@hotwired/stimulus'

import '../src/init/datatables'

import { handleAjaxError } from '../src/init/ajax'
import { handleClickableRowClick } from '../src/mixins/with_clickable_rows'

export default class extends Controller {
  static targets = ['datatable', 'listener', 'filter', 'summary']
  static values = {
    basePath: String,
    source: String,
    columns: Array
  }

  connect () {
    this.datatable = $(this.datatableTarget).DataTable({
      serverSide: true,
      ajax: {
        url: this.sourceValue,
        type: 'POST',
        data: this._buildAjaxData.bind(this),
        error: handleAjaxError
      },
      columns: this.columnsValue,
      pageLength: 10, // add a data-page-length attribute to override
      ordering: $(this.datatableTarget).is('[data-order]'),
      stateDuration: -1, // use session (per tab) storage
      stateSave: true, // remember the current configuration
      drawCallback: this._drawCallback.bind(this),
      createdRow: this._createdRowCallback.bind(this)
    })
  }

  disconnect () {
    this.datatable.destroy()
  }

  reload () {
    this.datatable.ajax.reload()
  }

  _buildAjaxData (data) {
    const filterData = {}
    this._eachFilterParam((key, value, multiple) => {
      if (multiple) {
        filterData[key] = (filterData[key] || []).concat(value)
      } else {
        filterData[key] = value
      }
    })
    data.filter = filterData
  }

  _drawCallback (data) {
    const json = data.json || {}

    // Broadcast points update to listeners
    if (json.points) {
      this.listenerTargets.forEach(listener => {
        this.dispatch('pointsUpdated', {
          target: listener,
          detail: { points: json.points }
        })
      })
    }

    // Broadcast the full datatable response to listeners (e.g. summary widgets)
    this.listenerTargets.forEach(listener => {
      this.dispatch('datatableUpdated', {
        target: listener,
        detail: json
      })
    })

    // Update summary content if provided
    if (json.filter_description && this.hasSummaryTarget) {
      this.summaryTarget.innerHTML = json.filter_description
    }

    // Highlight the summary when the filter is invalid (JSON response, so no Turbo update)
    if (this.hasSummaryTarget) {
      this.summaryTarget.classList.toggle('dt-filters__summary--invalid', Boolean(json.filter_invalid))
    }

    // Update the history state with current filters
    if (history && history.replaceState && this.hasBasePathValue) {
      const params = new URLSearchParams()
      this._eachFilterParam((key, value, multiple) => params.append(multiple ? `filter[${key}][]` : `filter[${key}]`, value))

      const url = new URL(this.basePathValue, window.location.origin)
      url.search = params.toString()

      const relativeUrl = url.pathname + url.search
      history.replaceState({ turbo: true, relativeUrl }, '', relativeUrl)
    }

    // Remove any existing error messages
    $(this.element).find('.is-invalid').removeClass('is-invalid')
    $(this.element).find('.form-group-invalid').removeClass('form-group-invalid')
    $(this.element).find('.invalid-feedback').remove()
  }

  _eachFilterParam (callback) {
    this.filterTargets.forEach(target => {
      if (target.multiple) {
        const match = target.name.match(/^filter\[(.+)\]\[\]$/)
        const key = match ? match[1] : target.name
        const selectedOptions = Array.from(target.selectedOptions)

        // Send a blank value when nothing is selected, so clearing a filter with a default survives a reload
        if (selectedOptions.length === 0) {
          callback(key, '', true)
        } else {
          selectedOptions.forEach(option => callback(key, option.value, true))
        }
      } else {
        const match = target.name.match(/^filter\[(.+)\]$/)
        const key = match ? match[1] : target.name
        callback(key, target.value, false)
      }
    })
  }

  // Add stimulus action to each row for clickable rows
  _createdRowCallback (row, data, dataIndex, cells) {
    row.dataset.action = 'click->filtered-remote-datatable#rowClicked'
  }

  // Finds the most relevant link to the user, and visits it (using Turbo)
  rowClicked (event) {
    handleClickableRowClick(event)
  }
}
